import React, { useEffect, useMemo, useState } from "react";
import { useAdminAuth } from "../AdminAuthContext";
import {
  PageHeader, Panel, Badge, Table, Button, Select,
} from "../components/UIKit";
import { Check, Play, Save, History, Code2, Eye, X, ArrowUp, ArrowDown, Minus } from "lucide-react";

export default function AdminAlgorithms() {
  const { api } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [selId, setSelId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [configText, setConfigText] = useState("");
  const [status, setStatus] = useState("draft");
  const [scope, setScope] = useState("all");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sampleSize, setSampleSize] = useState(30);

  const runPreview = async () => {
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const parsed = JSON.parse(configText);
      const res = await api("/algorithms/tips_engine/preview", {
        method: "POST",
        data: { candidate_config: parsed, sample_size: sampleSize },
      });
      setPreviewData(res);
    } catch (err) {
      setPreviewData({ error: err.message || "Errore preview" });
    } finally {
      setPreviewLoading(false);
    }
  };

  const load = () =>
    api("/algorithms").then((d) => {
      setItems(d.items || []);
      if (!selId && d.items?.length) setSelId(d.items[0].id);
    });

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!selId) return;
    api(`/algorithms/${selId}`).then((d) => {
      setDetail(d);
      setConfigText(JSON.stringify(d.algorithm.config, null, 2));
      setStatus(d.algorithm.status);
      setScope(d.algorithm.scope || "all");
    });
  }, [selId, api]);

  const save = async () => {
    setSaving(true);
    setMessage("");
    try {
      const parsed = JSON.parse(configText);
      await api(`/algorithms/${selId}`, {
        method: "PUT",
        data: { config: parsed, status, scope },
      });
      setMessage("Salvato");
      await load();
    } catch (err) {
      setMessage("Errore: JSON non valido o server error");
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(""), 2500);
    }
  };

  const activate = async () => {
    const ch = prompt("Changelog per questa attivazione:", "");
    if (ch === null) return;
    await api(`/algorithms/${selId}/activate`, {
      method: "POST",
      data: { scope, changelog: ch },
    });
    setMessage("Algoritmo attivato");
    setTimeout(() => setMessage(""), 2500);
    await load();
    const d = await api(`/algorithms/${selId}`);
    setDetail(d);
  };

  return (
    <div className="p-8">
      <PageHeader
        title="Gestione Algoritmi"
        subtitle="Versiona, configura e attiva gli algoritmi di elaborazione"
      />

      <div className="grid grid-cols-[340px,1fr] gap-4">
        <Panel title="Algoritmi">
          <div className="space-y-1.5">
            {items.map((a) => {
              const active = a.id === selId;
              return (
                <button
                  key={a.id}
                  onClick={() => setSelId(a.id)}
                  className={`w-full text-left rounded-lg border px-3 py-2.5 transition-colors ${
                    active
                      ? "bg-[#14c2a1]/10 border-[#14c2a1]/30"
                      : "bg-white/[0.03] border-white/10 hover:bg-white/5"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-white text-[13px] font-semibold">
                      {a.name}
                    </div>
                    <Badge
                      color={
                        a.status === "active"
                          ? "teal"
                          : a.status === "testing"
                          ? "amber"
                          : a.status === "deprecated"
                          ? "rose"
                          : "slate"
                      }
                    >
                      {a.status}
                    </Badge>
                  </div>
                  <div className="text-white/55 text-[11px] mt-0.5 flex items-center gap-2">
                    <span className="font-mono">{a.current_version}</span>
                    <span>·</span>
                    <span>{a.scope}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </Panel>

        {detail && (
          <div className="space-y-4">
            <Panel
              title={detail.algorithm.name}
              right={
                <div className="flex items-center gap-2">
                  <span className="text-white/55 text-[11px]">
                    autore: {detail.algorithm.author}
                  </span>
                  <Badge color="slate">
                    {detail.algorithm.current_version}
                  </Badge>
                </div>
              }
            >
              <p className="text-white/80 text-[13px] leading-snug">
                {detail.algorithm.description}
              </p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div>
                  <label className="text-white/60 text-[11px] uppercase tracking-wider">
                    Stato
                  </label>
                  <div className="mt-1">
                    <Select
                      value={status}
                      onChange={setStatus}
                      options={[
                        { value: "draft", label: "Draft" },
                        { value: "testing", label: "Testing" },
                        { value: "active", label: "Active" },
                        { value: "deprecated", label: "Deprecated" },
                      ]}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-white/60 text-[11px] uppercase tracking-wider">
                    Scope attivazione
                  </label>
                  <div className="mt-1">
                    <Select
                      value={scope}
                      onChange={setScope}
                      options={[
                        { value: "all", label: "Tutti gli utenti" },
                        { value: "beta", label: "Gruppo beta" },
                        { value: "early_adopters", label: "Early adopters" },
                        { value: "internal", label: "Solo interni" },
                        { value: "firmware:2.0.0-beta", label: "Firmware 2.0.0-beta" },
                      ]}
                    />
                  </div>
                </div>
              </div>
            </Panel>

            <Panel
              title="Configurazione JSON"
              right={
                <div className="flex items-center gap-2">
                  {message && (
                    <span className="text-[12px] text-white/75">{message}</span>
                  )}
                  {detail.algorithm.algorithm_id === "tips_engine" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setPreviewOpen(true); runPreview(); }}
                      data-testid="tip-preview-btn"
                    >
                      <Eye size={13} /> Anteprima {sampleSize} sessioni
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={save} disabled={saving}>
                    <Save size={13} /> Salva config
                  </Button>
                  <Button variant="success" size="sm" onClick={activate}>
                    <Check size={13} /> Attiva ora
                  </Button>
                </div>
              }
            >
              <textarea
                value={configText}
                onChange={(e) => setConfigText(e.target.value)}
                rows={14}
                spellCheck={false}
                className="w-full rounded-lg bg-[#0a1317] border border-white/10 focus:border-[#14c2a1] text-[#c9fde9] text-[12.5px] font-mono p-3 focus:outline-none resize-y"
              />
            </Panel>

            <Panel
              title="Versioni"
              right={
                <span className="text-white/55 text-[11px] flex items-center gap-1">
                  <History size={12} /> {detail.versions?.length || 0} entries
                </span>
              }
            >
              <Table
                columns={[
                  {
                    key: "version",
                    label: "Versione",
                    render: (r) => <span className="font-mono">{r.version}</span>,
                  },
                  { key: "changelog", label: "Changelog" },
                  { key: "author", label: "Autore" },
                  {
                    key: "created_at",
                    label: "Data",
                    render: (r) =>
                      new Date(r.created_at).toLocaleString("it-IT", {
                        day: "2-digit",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      }),
                  },
                ]}
                rows={detail.versions || []}
              />
            </Panel>
          </div>
        )}
      </div>

      {/* Tip Preview modal */}
      {previewOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setPreviewOpen(false)}
          data-testid="tip-preview-modal"
        >
          <div
            className="w-[min(900px,95vw)] max-h-[85vh] overflow-y-auto rounded-2xl bg-[#0e1517] border border-white/10 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
              <div>
                <h3 className="text-white text-[15px] font-semibold">
                  Tip Preview — config candidato vs corrente
                </h3>
                <p className="text-white/50 text-[11.5px] mt-0.5">
                  Simula il tips_engine sulle sessioni recenti con il JSON in editor.
                  Nessun dato viene salvato; è un dry-run.
                </p>
              </div>
              <button
                onClick={() => setPreviewOpen(false)}
                className="h-8 w-8 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 flex items-center justify-center"
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                <label className="text-white/60 text-[11px] uppercase tracking-wider">
                  Dimensione campione
                </label>
                {[10, 30, 100, 200].map((n) => (
                  <button
                    key={n}
                    onClick={() => setSampleSize(n)}
                    className={`h-7 px-3 rounded-md text-[11.5px] font-medium border transition-all ${
                      sampleSize === n
                        ? "bg-[#14c2a1]/15 border-[#14c2a1]/40 text-[#67e8d1]"
                        : "bg-white/5 border-white/10 text-white/55 hover:text-white"
                    }`}
                  >
                    {n}
                  </button>
                ))}
                <Button variant="ghost" size="sm" onClick={runPreview} disabled={previewLoading}>
                  <Play size={12} /> {previewLoading ? "Calcolo…" : "Ricalcola"}
                </Button>
              </div>

              {previewLoading && !previewData && (
                <div className="text-white/55 text-sm py-10 text-center">
                  Esecuzione dry-run in corso…
                </div>
              )}
              {previewData?.error && (
                <div className="rounded-lg bg-rose-500/10 border border-rose-400/30 text-rose-200 px-4 py-3 text-sm">
                  {previewData.error}
                </div>
              )}
              {previewData && !previewData.error && (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <PreviewStat label="Sessioni analizzate" value={previewData.sample_size} />
                    <PreviewStat
                      label="Fires totali — corrente"
                      value={previewData.total_fires_current}
                      color="slate"
                    />
                    <PreviewStat
                      label="Fires totali — candidato"
                      value={previewData.total_fires_candidate}
                      color={
                        previewData.total_fires_candidate > previewData.total_fires_current
                          ? "amber"
                          : "teal"
                      }
                    />
                  </div>

                  <div className="rounded-lg border border-white/10 overflow-hidden">
                    <table className="w-full text-[12.5px]">
                      <thead className="bg-white/[0.03] text-white/55 text-[10.5px] uppercase tracking-wider">
                        <tr>
                          <th className="text-left px-3 py-2">Trigger</th>
                          <th className="text-right px-3 py-2">Corrente</th>
                          <th className="text-right px-3 py-2">Candidato</th>
                          <th className="text-right px-3 py-2">Δ</th>
                          <th className="text-right px-3 py-2">Utenti (cand.)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.rows.map((r) => (
                          <tr key={r.trigger_id} className="border-t border-white/5">
                            <td className="px-3 py-2 text-white font-mono text-[11.5px]">
                              {r.trigger_id}
                            </td>
                            <td className="px-3 py-2 text-right text-white/70">
                              {r.current_fires}
                            </td>
                            <td className="px-3 py-2 text-right text-white">
                              {r.candidate_fires}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <DeltaCell value={r.delta} />
                            </td>
                            <td className="px-3 py-2 text-right text-white/60">
                              {r.candidate_users}
                            </td>
                          </tr>
                        ))}
                        {previewData.rows.length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-3 py-6 text-center text-white/45">
                              Nessun trigger scattato su questo campione
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-white/40 text-[11px]">
                    Δ positivo = il config candidato invia <em>più</em> tips agli utenti.
                    Δ negativo = ne invia meno. Usa questa anteprima per tarare le soglie
                    senza rischio prima di salvare.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PreviewStat({ label, value, color = "slate" }) {
  const colors = {
    slate: "text-white/85",
    teal: "text-[#67e8d1]",
    amber: "text-amber-300",
  };
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3">
      <div className="text-white/50 text-[10px] uppercase tracking-wider">{label}</div>
      <div className={`text-[24px] font-semibold mt-0.5 ${colors[color]}`}>{value}</div>
    </div>
  );
}

function DeltaCell({ value }) {
  if (value === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-white/45 font-mono">
        <Minus size={11} /> 0
      </span>
    );
  }
  const Icon = value > 0 ? ArrowUp : ArrowDown;
  const cls = value > 0 ? "text-amber-300" : "text-[#67e8d1]";
  return (
    <span className={`inline-flex items-center gap-1 font-mono ${cls}`}>
      <Icon size={11} /> {value > 0 ? "+" : ""}{value}
    </span>
  );
}
